import { Directive, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';

/**
 * Hybrid General Data-Cy Button Directive
 * 1. Propagator: If placed on a parent with [data-cy/uniqueId/name/placeholder], it labels children.
 * 2. Climber: If placed on a button without data-cy, it searches upwards for a name.
 */
@Directive({
  // Targeting all buttons across the entire app
  selector: '[data-cy], [uniqueId], button:not([data-cy]), [mat-button]:not([data-cy]), [mat-flat-button]:not([data-cy]), [mat-raised-button]:not([data-cy]), [mat-stroked-button]:not([data-cy])'
})
export class DataCyButtonDirective implements AfterViewInit, OnDestroy {
  private observer: MutationObserver | null = null;

  constructor(private el: ElementRef<HTMLElement>) {}

  ngAfterViewInit(): void {
    this.apply();
    this.observer = new MutationObserver(() => this.apply());
    this.observer.observe(this.el.nativeElement, { childList: true, subtree: true });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  private apply(): void {
    const host = this.el.nativeElement;
    
    // Base name priority for identifying elements
    const baseCy = host.getAttribute('data-cy') || 
                   host.getAttribute('uniqueId') || 
                   host.getAttribute('name') ||
                   host.getAttribute('placeholder')?.toLowerCase().replace(/\s+/g, '-');

    if (baseCy) {
      const targets = host.querySelectorAll<HTMLElement>(
        'button:not([data-cy]), [mat-button]:not([data-cy]), [mat-flat-button]:not([data-cy])'
      );
      targets.forEach((target, i) => {
        const tag = target.tagName.toLowerCase().replace('mat-', '');
        const suffix = i === 0 ? `__${tag}` : `__${tag}-${i + 1}`;
        target.setAttribute('data-cy', `${baseCy}${suffix}`);
      });
    } 
    
    // Climber logic
    const parentWithCy = host.closest('[data-cy], [uniqueId], [name]');
    if (parentWithCy && !host.hasAttribute('data-cy')) {
        const parentName = parentWithCy.getAttribute('data-cy') || 
                           parentWithCy.getAttribute('uniqueId') || 
                           parentWithCy.getAttribute('name');

        const tag = host.tagName.toLowerCase().replace('mat-', '');
        const similar = Array.from(parentWithCy.querySelectorAll(host.tagName.toLowerCase()));
        const index = similar.indexOf(host);
        const suffix = index === 0 ? `__${tag}` : `__${tag}-${index + 1}`;
        host.setAttribute('data-cy', `${parentName}${suffix}`);
    }
  }
}